import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API_BASE = 'https://martianbluecyberdefense.in';
const genNums = () => ({ n1: Math.floor(Math.random()*10)+1, n2: Math.floor(Math.random()*10)+1 });

const MathCaptcha = ({ onVerified, verified, resetKey }) => {
  const [nums, setNums] = useState(genNums);
  const [answer, setAnswer] = useState('');
  const [error, setErr] = useState('');
  useEffect(() => { setNums(genNums()); setAnswer(''); setErr(''); }, [resetKey]);
  const verify = () => {
    if (parseInt(answer) === nums.n1 + nums.n2) { onVerified(true); setErr(''); }
    else { setErr('Incorrect. Try again.'); setAnswer(''); setNums(genNums()); onVerified(false); }
  };
  return (
    <div style={{ borderRadius:12, padding:16, background:'rgba(0,212,255,0.05)', border:`2px solid ${verified?'rgba(0,255,100,0.5)':'rgba(0,212,255,0.3)'}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
        <span>🔐</span><span style={{ color:'#fff', fontFamily:'Rajdhani,sans-serif', fontWeight:600, fontSize:15 }}>Security Verification</span>
      </div>
      {verified ? (
        <div style={{ display:'flex', alignItems:'center', gap:8 }}><span>✅</span><span style={{ color:'#00ff64', fontFamily:'Poppins,sans-serif', fontSize:14 }}>Verified!</span></div>
      ) : (
        <>
          <p style={{ color:'#d1d5db', fontFamily:'Poppins,sans-serif', fontSize:13, marginBottom:10 }}>Solve to confirm you're human:</p>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <span style={{ fontWeight:700, fontSize:18, padding:'6px 14px', borderRadius:8, color:'#00d4ff', background:'rgba(0,212,255,0.1)', border:'1px solid rgba(0,212,255,0.3)', fontFamily:'Rajdhani,sans-serif', letterSpacing:2 }}>{nums.n1} + {nums.n2} = ?</span>
            <input type="number" value={answer} onChange={e=>setAnswer(e.target.value)} autoComplete="off" onKeyDown={e=>e.key==='Enter'&&verify()} placeholder="Answer"
              style={{ width:80, padding:'7px 10px', borderRadius:8, textAlign:'center', background:'#061c36', border:'1px solid rgba(0,212,255,0.4)', color:'#fff', fontFamily:'Poppins,sans-serif', fontSize:14, outline:'none', boxSizing:'border-box' }} />
            <button onClick={verify} type="button" style={{ padding:'7px 14px', borderRadius:8, fontWeight:600, cursor:'pointer', background:'linear-gradient(135deg,#00d4ff,#00b4d8)', color:'#000', fontFamily:'Rajdhani,sans-serif', fontSize:13, border:'none' }}>Verify</button>
            <button onClick={()=>{setNums(genNums());setAnswer('');setErr('');onVerified(false);}} type="button"
              style={{ padding:'7px 10px', borderRadius:8, cursor:'pointer', background:'rgba(0,212,255,0.1)', border:'1px solid rgba(0,212,255,0.3)', color:'#00d4ff', fontSize:15 }}>🔄</button>
          </div>
          {error && <p style={{ color:'#ff6b6b', fontFamily:'Poppins,sans-serif', fontSize:12, marginTop:8 }}>{error}</p>}
        </>
      )}
    </div>
  );
};

const SoftwareSecurityWebinar = () => {
  const canvasRef = useRef(null);
  const emptyForm = { fullName:'', email:'', mobile:'', currentStatus:'', collegeName:'', priorKnowledge:'', attendLive:'', agreeToReceive:'' };
  const [formData, setFormData] = useState(emptyForm);
  const [status, setStatus] = useState({ type:'', message:'' });
  const [loading, setLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const handle = e => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const resetForm = useCallback(() => {
    setFormData(emptyForm); setCaptchaVerified(false); setCaptchaResetKey(k => k+1);
  }, []);

  const submit = async () => {
    if (!captchaVerified) { setStatus({ type:'error', message:'Please complete the security verification.' }); return; }
    const required = ['fullName','email','mobile','currentStatus','collegeName','priorKnowledge','attendLive','agreeToReceive'];
    if (required.some(f => !formData[f])) { setStatus({ type:'error', message:'Please fill in all required fields.' }); return; }
    setLoading(true); setStatus({ type:'', message:'' });
    try {
      const res = await axios.post(`${API_BASE}/api/software-security`, formData, { timeout:8000, headers:{'Content-Type':'application/json'} });
      if (res.data.success) {
        setStatus({ type:'success', message:'🎉 Registration successful! Confirmation email sent.' });
        resetForm();
        setTimeout(() => setStatus({ type:'', message:'' }), 6000);
      } else { setStatus({ type:'error', message: res.data.message || 'Registration failed.' }); }
    } catch (err) {
      if (err.response?.status === 409) { setStatus({ type:'error', message:'⚠️ ' + (err.response.data?.message || 'Already registered.') }); }
      else { setStatus({ type:'error', message:`Error: ${err.message}` }); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    if (window.innerWidth < 768) return;
    const ctx = canvas.getContext('2d'); let raf;
    const resize = () => { canvas.width=window.innerWidth; canvas.height=window.innerHeight; };
    resize(); window.addEventListener('resize', resize);
    class Node { constructor(){this.x=Math.random()*canvas.width;this.y=Math.random()*canvas.height;this.vx=(Math.random()-0.5)*0.8;this.vy=(Math.random()-0.5)*0.8;this.r=Math.random()*2.5+1;this.phase=Math.random()*Math.PI*2;} tick(){this.x+=this.vx;this.y+=this.vy;this.phase+=0.03;if(this.x<0)this.x=canvas.width;if(this.x>canvas.width)this.x=0;if(this.y<0)this.y=canvas.height;if(this.y>canvas.height)this.y=0;} draw(){const p=Math.sin(this.phase)*0.5+0.5,s=this.r+p*1.5;ctx.beginPath();ctx.arc(this.x,this.y,s,0,Math.PI*2);ctx.fillStyle=`rgba(0,210,230,${0.5+p*0.15})`;ctx.fill();} }
    const nodes=Array.from({length:40},()=>new Node());
    const animate=()=>{ctx.fillStyle='rgba(10,37,64,0.12)';ctx.fillRect(0,0,canvas.width,canvas.height);nodes.forEach(n=>{n.tick();n.draw();});raf=requestAnimationFrame(animate);};
    animate();
    return ()=>{window.removeEventListener('resize',resize);cancelAnimationFrame(raf);};
  }, []);

  const INP = { display:'block', width:'100%', boxSizing:'border-box', padding:'11px 14px', borderRadius:8, background:'#061c36', border:'1.5px solid rgba(0,212,255,0.4)', color:'#ffffff', fontFamily:'Poppins,sans-serif', fontSize:14, outline:'none' };
  const onFocus = e => { e.target.style.borderColor='#00d4ff'; e.target.style.boxShadow='0 0 0 3px rgba(0,212,255,0.2)'; };
  const onBlur  = e => { e.target.style.borderColor='rgba(0,212,255,0.4)'; e.target.style.boxShadow='none'; };
  const fadeUp = { hidden:{opacity:0,y:30}, visible:{opacity:1,y:0,transition:{duration:0.5}} };

  const radioGroups = [
    { field:'currentStatus',  label:'Current Status',                              options:['Student','Working Professional','Job Seeker','Other'] },
    { field:'priorKnowledge', label:'Prior Knowledge of Cybersecurity?',           options:['Beginner','Intermediate','Advanced'] },
    { field:'attendLive',     label:'Will you attend live?',                       options:['Yes','No','Maybe'] },
    { field:'agreeToReceive', label:'Agree to receive updates on Email/WhatsApp?', options:['Yes','No'] },
  ];

  return (
    <div style={{ position:'relative', minHeight:'100vh', background:'linear-gradient(135deg,#0a2540 0%,#0d3a5c 35%,#115073 65%,#0d3a5c 100%)' }}>
      <canvas ref={canvasRef} style={{ position:'fixed', top:0, left:0, width:'100vw', height:'100vh', pointerEvents:'none', zIndex:0 }} />
      <div style={{ position:'relative', zIndex:1, maxWidth:1200, margin:'0 auto', padding:'100px 20px 60px' }}>

        {/* POSTER */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp} style={{ marginBottom:28, borderRadius:16, overflow:'hidden', boxShadow:'0 8px 40px rgba(0,212,255,0.2)', maxWidth:700, margin:'0 auto 28px' }}>
          <img src="/images/software-security-poster.jpg" alt="Software Security Testing Webinar" style={{ width:'100%', display:'block', borderRadius:16 }} />
        </motion.div>

        {/* HEADING */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp} style={{ textAlign:'center', marginBottom:28 }}>
          <span style={{ color:'#00b4d8', fontFamily:'Poppins,sans-serif', fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', display:'block', marginBottom:6 }}>FREE WEBINAR — APRIL 2026</span>
          <h1 style={{ color:'#fff', fontFamily:'Rajdhani,sans-serif', fontSize:'clamp(1.6rem,4vw,2.8rem)', fontWeight:700, margin:0, lineHeight:1.15, textShadow:'0 0 24px rgba(0,212,255,0.4)' }}>
            Software Security <span style={{ color:'#00d4ff' }}>Testing Webinar</span>
          </h1>
          <p style={{ color:'#9ca3af', fontFamily:'Poppins,sans-serif', fontSize:14, marginTop:10 }}>Hands-on session on software security testing by industry professionals — Free to attend</p>
        </motion.div>

        {/* TWO COLUMN GRID */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))', gap:24, alignItems:'start' }}>

          {/* LEFT — Registration Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{once:true}} variants={fadeUp}
            style={{ background:'rgba(6,28,54,0.85)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:20, padding:'28px 24px', backdropFilter:'blur(10px)' }}>

            <h3 style={{ color:'#fff', fontFamily:'Rajdhani,sans-serif', fontSize:24, fontWeight:700, margin:'0 0 10px' }}>Register for the Webinar</h3>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
              <span style={{ padding:'3px 12px', borderRadius:999, background:'rgba(0,212,255,0.12)', border:'1px solid rgba(0,212,255,0.35)', color:'#00d4ff', fontFamily:'Poppins,sans-serif', fontSize:12, fontWeight:600 }}>📅 4th April 2026</span>
              <span style={{ padding:'3px 12px', borderRadius:999, background:'rgba(0,212,255,0.12)', border:'1px solid rgba(0,212,255,0.35)', color:'#00d4ff', fontFamily:'Poppins,sans-serif', fontSize:12, fontWeight:600 }}>⏰ 1:00 PM – 2:00 PM</span>
              <span style={{ padding:'3px 12px', borderRadius:999, background:'rgba(0,255,128,0.1)', border:'1px solid rgba(0,255,128,0.3)', color:'#00ff80', fontFamily:'Poppins,sans-serif', fontSize:12, fontWeight:600 }}>💻 Online | FREE</span>
            </div>
            <p style={{ color:'#9ca3af', fontFamily:'Poppins,sans-serif', fontSize:12, marginBottom:18 }}>* All fields are required</p>

            {status.message && (
              <div style={{ marginBottom:16, padding:'12px 14px', borderRadius:8, background:status.type==='success'?'rgba(34,197,94,0.15)':'rgba(239,68,68,0.15)', border:`1px solid ${status.type==='success'?'#22c55e':'#ef4444'}`, color:status.type==='success'?'#4ade80':'#f87171', fontFamily:'Poppins,sans-serif', fontSize:13 }}>
                {status.message}
              </div>
            )}

            {[
              { name:'fullName',    label:'Full Name',              type:'text',  ph:'Your full name' },
              { name:'email',       label:'Email Address',          type:'email', ph:'Your email' },
              { name:'mobile',      label:'Mobile Number',          type:'tel',   ph:'Your mobile number' },
              { name:'collegeName', label:'College / Company Name', type:'text',  ph:'College or company' },
            ].map(f => (
              <div key={f.name} style={{ marginBottom:16 }}>
                <label style={{ display:'block', color:'#d1d5db', fontFamily:'Poppins,sans-serif', fontSize:13, marginBottom:6 }}>{f.label} <span style={{ color:'#00d4ff' }}>*</span></label>
                <input type={f.type} name={f.name} value={formData[f.name]} onChange={handle} onFocus={onFocus} onBlur={onBlur} placeholder={f.ph} autoComplete="off" style={INP} />
              </div>
            ))}

            {radioGroups.map(({ field, label, options }) => (
              <div key={field} style={{ marginBottom:16 }}>
                <label style={{ display:'block', color:'#d1d5db', fontFamily:'Poppins,sans-serif', fontSize:13, marginBottom:8 }}>{label} <span style={{ color:'#00d4ff' }}>*</span></label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {options.map(opt => {
                    const active = formData[field] === opt;
                    return (
                      <label key={opt} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', padding:'6px 12px', borderRadius:8, border:`1.5px solid ${active?'#00d4ff':'rgba(0,212,255,0.3)'}`, background:active?'rgba(0,212,255,0.15)':'rgba(5,20,40,0.8)', color:active?'#00d4ff':'#d1d5db', fontFamily:'Poppins,sans-serif', fontSize:13, userSelect:'none' }}>
                        <input type="radio" name={field} value={opt} checked={active} onChange={handle} style={{ display:'none' }} />
                        <span style={{ width:14, height:14, borderRadius:'50%', border:`2px solid ${active?'#00d4ff':'rgba(0,212,255,0.4)'}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          {active && <span style={{ width:7, height:7, borderRadius:'50%', background:'#00d4ff' }} />}
                        </span>
                        {opt}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}

            <div style={{ marginBottom:20 }}>
              <MathCaptcha onVerified={setCaptchaVerified} verified={captchaVerified} resetKey={captchaResetKey} />
            </div>

            <button type="button" onClick={submit} disabled={loading}
              style={{ width:'100%', padding:'13px 0', borderRadius:12, border:'none', cursor:loading?'not-allowed':'pointer', background:loading?'rgba(0,212,255,0.2)':'linear-gradient(135deg,#00d4ff,#00b4d8)', color:loading?'#7ab8d4':'#000', fontFamily:'Rajdhani,sans-serif', fontSize:18, fontWeight:800, letterSpacing:1, transition:'all 0.3s' }}>
              {loading ? '⏳ Registering...' : '🚀 Register Now — FREE'}
            </button>
            <p style={{ color:'#4a7a9b', fontFamily:'Poppins,sans-serif', fontSize:12, textAlign:'center', marginTop:12 }}>🔒 Your information is safe with us. No spam.</p>
          </motion.div>

          {/* RIGHT — About Section */}
          <motion.div initial="hidden" whileInView="visible" viewport={{once:true}} variants={fadeUp} style={{ display:'flex', flexDirection:'column', gap:20 }}>

            {/* About the Webinar */}
            <div style={{ background:'rgba(6,28,54,0.85)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:20, padding:'28px 24px', backdropFilter:'blur(10px)' }}>
              <h3 style={{ color:'#fff', fontFamily:'Rajdhani,sans-serif', fontSize:24, fontWeight:700, marginBottom:18 }}>About the Webinar</h3>

              {[
                {
                  icon:'🎯', color:'#00d4ff', title:'What is this Webinar?',
                  body:'A free, live, 1-hour online session covering real-world software security testing techniques — taught by practicing cybersecurity professionals with industry experience.'
                },
                {
                  icon:'📚', color:'#00b4d8', title:'What You Will Learn',
                  list:[
                    'Introduction to Software Security Testing',
                    'Common Vulnerabilities — OWASP Top 10',
                    'Static & Dynamic Analysis Techniques',
                    'Secure Code Review Best Practices',
                    'Real-world case studies & live demos',
                  ]
                },
                {
                  icon:'👥', color:'#7dd3fc', title:'Who Should Attend',
                  list:[
                    'CS / IT students and fresh graduates',
                    'Software developers & QA engineers',
                    'Cybersecurity aspirants & job seekers',
                    'Anyone interested in secure software development',
                  ]
                },
                {
                  icon:'🏆', color:'#34d399', title:'Key Benefits',
                  list:[
                    'Completely FREE — no registration fee',
                    'Live interactive Q&A with the speaker',
                    'Certificate of participation',
                    'Meeting link sent to your email',
                    'WhatsApp updates & study material',
                  ]
                },
              ].map(({ icon, color, title, body, list }) => (
                <div key={title} style={{ marginBottom:14, padding:'14px 16px', borderRadius:12, background:'rgba(0,212,255,0.06)', border:'1px solid rgba(0,212,255,0.15)' }}>
                  <h4 style={{ color, fontFamily:'Rajdhani,sans-serif', fontSize:16, fontWeight:700, marginBottom:8 }}>{icon} {title}</h4>
                  {body && <p style={{ color:'#c2ccd8', fontFamily:'Poppins,sans-serif', fontSize:13, lineHeight:1.7, margin:0 }}>{body}</p>}
                  {list && (
                    <ul style={{ listStyle:'none', padding:0, margin:0 }}>
                      {list.map(item => (
                        <li key={item} style={{ color:'#cbd5e1', fontFamily:'Poppins,sans-serif', fontSize:13, marginBottom:4, display:'flex', alignItems:'flex-start', gap:6 }}>
                          <span style={{ color:'#00d4ff', marginTop:2, flexShrink:0 }}>▸</span>{item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* Trainer Card */}
            <div style={{ background:'rgba(6,28,54,0.85)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:20, padding:'24px', backdropFilter:'blur(10px)' }}>
              <h3 style={{ color:'#fff', fontFamily:'Rajdhani,sans-serif', fontSize:20, fontWeight:700, marginBottom:14 }}>🎓 About the Trainer</h3>
              <div style={{ display:'flex', alignItems:'flex-start', gap:16 }}>
                <div style={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg,#00d4ff,#0077aa)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>👨‍💻</div>
                <div>
                  <p style={{ color:'#00d4ff', fontFamily:'Rajdhani,sans-serif', fontSize:17, fontWeight:700, margin:'0 0 4px' }}>Industry Professional</p>
                  <p style={{ color:'#9ca3af', fontFamily:'Poppins,sans-serif', fontSize:13, lineHeight:1.6, margin:0 }}>Experienced cybersecurity practitioner with hands-on expertise in software security testing, penetration testing, and secure development practices. Mentor to hundreds of cybersecurity professionals.</p>
                </div>
              </div>
            </div>

            {/* About Martian Blue */}
            <div style={{ background:'rgba(6,28,54,0.85)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:20, padding:'24px', backdropFilter:'blur(10px)' }}>
              <h3 style={{ color:'#fff', fontFamily:'Rajdhani,sans-serif', fontSize:20, fontWeight:700, marginBottom:10 }}>About Martian Blue Cyber Defense</h3>
              <p style={{ color:'#9ca3af', fontFamily:'Poppins,sans-serif', fontSize:13, lineHeight:1.7, margin:'0 0 16px' }}>Martian Blue Cyber Defense is a cybersecurity education and services company committed to training the next generation of cyber professionals. We conduct webinars, summer schools, and professional training programs with industry experts and researchers.</p>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {[
                  '📧 cyberedu@martianbluecyberdefense.in',
                  '📞 +91 94413 14231',
                  '🌐 martianbluecyberdefense.in',
                ].map(c => (
                  <span key={c} style={{ color:'#7ab8d4', fontFamily:'Poppins,sans-serif', fontSize:12 }}>{c}</span>
                ))}
              </div>
            </div>

          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SoftwareSecurityWebinar;
